import React, { useState, useEffect } from 'react';
import { Alert, Button, Card, Badge, Spinner } from 'react-bootstrap';
import { getPendingInvites, acceptInvite, declineInvite } from '../utils/firebase';

function InvitesPanel({ user, onInviteAccepted }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    if (user?.email) {
      loadInvites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadInvites = async () => {
    try {
      const pendingInvites = await getPendingInvites(user.email);
      setInvites(pendingInvites);
    } catch (err) {
      console.error('Error loading invites:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invite) => {
    setProcessingId(invite.id);
    try {
      await acceptInvite(invite.id, user.uid, user.email);
      setInvites((prev) => prev.filter((inv) => inv.id !== invite.id));
      if (onInviteAccepted) {
        onInviteAccepted(invite);
      }
    } catch (err) {
      console.error('Error accepting invite:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (inviteId) => {
    setProcessingId(inviteId);
    try {
      await declineInvite(inviteId);
      setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
    } catch (err) {
      console.error('Error declining invite:', err);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return null;
  }

  if (invites.length === 0) {
    return null;
  }

  return (
    <div className="invites-panel mb-3">
      <Alert variant="info" className="mb-0">
        <div className="d-flex align-items-center gap-2 mb-2">
          <i className="bi bi-envelope-paper"></i>
          <strong>Project Invitations ({invites.length})</strong>
        </div>
        <div className="invites-list">
          {invites.map((invite) => (
            <Card key={invite.id} className="invite-card mb-2">
              <Card.Body className="py-2 px-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="fw-medium">
                      {invite.sessionTitle || 'Untitled Project'}
                    </div>
                    <small className="text-muted">
                      From {invite.ownerEmail} &middot;{' '}
                      <Badge bg={invite.role === 'editor' ? 'primary' : 'secondary'}>
                        {invite.role}
                      </Badge>
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    {processingId === invite.id ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleAccept(invite)}
                          title="Accept invite"
                        >
                          <i className="bi bi-check-lg"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDecline(invite.id)}
                          title="Decline invite"
                        >
                          <i className="bi bi-x-lg"></i>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      </Alert>
    </div>
  );
}

export default InvitesPanel;
